import { useState, useId, forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import styles from './SecretInput.module.css';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * Input para SECRETOS técnicos (API keys, PATs, tokens) — NO para passwords
 * de login.
 *
 * Diferencia clave vs `PasswordInput`: usa `type="text"` y oculta los
 * caracteres con CSS (`-webkit-text-security: disc`). Esto evita que los
 * password managers (Comet built-in, 1Password, Bitwarden, Apple keychain)
 * lo detecten como password field e intercepten el value al submit —
 * causa del bug del wizard PAT 2026-05-25/26 donde el PAT pegado se
 * borraba antes de llegar al PATCH.
 *
 * Trick adicional: `name` aleatorio en cada mount (vía useId) + autocomplete
 * "off" + spellcheck off. Ningún password store lo asocia con un dominio.
 *
 * Visual: 100% idéntico a PasswordInput (mismo toggle eye/eye-off). El
 * usuario no nota la diferencia, pero el browser sí.
 */
export const SecretInput = forwardRef<HTMLInputElement, Props>(
  function SecretInput({ className = '', name, autoComplete, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    const randomId = useId();
    // Si el caller no fuerza un name, generamos uno random para confundir
    // a los password managers. Si fuerza uno, respetamos (caller knows best).
    const safeName = name ?? `secret-${randomId.replace(/:/g, '')}`;
    return (
      <div className={styles.wrap}>
        <input
          ref={ref}
          type="text"
          name={safeName}
          autoComplete={autoComplete ?? 'off'}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          className={`${className} ${styles.input} ${visible ? '' : styles.masked}`}
          {...rest}
        />
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Ocultar secreto' : 'Mostrar secreto'}
          aria-pressed={visible}
          tabIndex={-1}
        >
          {visible ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
        </button>
      </div>
    );
  },
);
